import type { ReactNode } from "react";
import { Platform } from "react-native";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";

import type { SupportSubmissionKind } from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";

import { styles } from "../AppShell.styles.js";
import { LineIcon } from "../line-icons.js";
import {
  patientDesktopPalette,
  patientMobilePalette,
  type AppShellTheme,
} from "../shell-core.js";
import { createWebGradientBackgroundStyle } from "../shell-primitives.js";
import type { ChatMessageViewModel } from "../types.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

export type PatientDesktopFooterLinkId = "terms" | "privacy" | "bug" | "feedback";

export const patientDesktopFooterLinks: readonly {
  id: PatientDesktopFooterLinkId;
  labelText: string;
}[] = [
  { id: "terms", labelText: "Terms of use" },
  { id: "privacy", labelText: "Privacy policy" },
  { id: "bug", labelText: "Report a bug" },
  { id: "feedback", labelText: "Send feedback" },
];

export type PatientSupportSentiment = "helpful" | "needs-work";

export type PatientSupportModalKind = PatientDesktopFooterLinkId | "chat-feedback";

export type PatientSupportModalState = {
  kind: PatientSupportModalKind;
  source: string;
  sentiment?: PatientSupportSentiment | null;
  message?: ChatMessageViewModel | null;
};

export type PatientSupportFormField =
  | "name"
  | "emailAddress"
  | "feedbackText"
  | "bugContext"
  | "bugImpact";

export type PatientSupportFormState = Record<PatientSupportFormField, string> & {
  errorText: string | null;
  isSubmitting: boolean;
  receiptId: string | null;
  successText: string | null;
};

type PatientSupportPolicyDocument = {
  markdownUrl: string;
  pdfUrl: string;
  title: string;
};

const patientSupportPolicyDocuments: Record<
  Extract<PatientSupportModalKind, "privacy" | "terms">,
  PatientSupportPolicyDocument
> = {
  privacy: {
    markdownUrl: "https://d12niuevuci1sc.cloudfront.net/privacy-policy.md",
    pdfUrl: "https://d12niuevuci1sc.cloudfront.net/privacy-policy.pdf",
    title: "Privacy policy",
  },
  terms: {
    markdownUrl: "https://d12niuevuci1sc.cloudfront.net/terms-of-use.md",
    pdfUrl: "https://d12niuevuci1sc.cloudfront.net/terms-of-use.pdf",
    title: "Terms of use",
  },
};

export function createPatientSupportInitialFormState(
  modal: PatientSupportModalState | null = null,
): PatientSupportFormState {
  const feedbackText =
    modal?.kind === "chat-feedback" && modal.sentiment === "helpful"
      ? `This ${brand.assistantName} answer was helpful.`
      : modal?.kind === "chat-feedback" && modal.sentiment === "needs-work"
        ? `This ${brand.assistantName} answer needs work because `
        : "";

  return {
    bugContext: "",
    bugImpact: "",
    emailAddress: "",
    errorText: null,
    feedbackText,
    isSubmitting: false,
    name: "",
    receiptId: null,
    successText: null,
  };
}

function getPatientSupportModalTitle(kind: PatientSupportModalKind): string {
  switch (kind) {
    case "bug":
      return "Report a bug";
    case "chat-feedback":
      return "Send feedback";
    case "feedback":
      return "Send feedback";
    case "privacy":
      return "Privacy policy";
    case "terms":
      return "Terms of use";
    default:
      return "Support";
  }
}

export function resolvePatientSupportSubmissionKind(
  modal: PatientSupportModalState,
): SupportSubmissionKind {
  if (modal.kind === "bug") {
    return "bug-report";
  }

  if (modal.kind === "chat-feedback") {
    return "chat-feedback";
  }

  return "feedback";
}

function openPatientSupportExternalUrl(url: string): void {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function getPatientSupportPageUrl(): string | null {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return null;
  }

  return window.location.href;
}

export function getPatientSupportUserAgent(): string | null {
  if (Platform.OS !== "web" || typeof navigator === "undefined") {
    return null;
  }

  return navigator.userAgent;
}

export function isValidPatientSupportEmail(emailAddress: string): boolean {
  return (
    emailAddress.length === 0 || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailAddress)
  );
}

export function renderPatientSupportModal(input: {
  formState: PatientSupportFormState;
  layout: typeof designTokens.semanticLayout;
  modalState: PatientSupportModalState | null;
  onChangeField: (field: PatientSupportFormField, value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}): ReactNode {
  const {
    formState,
    layout,
    modalState,
    onChangeField,
    onClose,
    onSubmit,
    theme,
    tokens,
  } = input;

  if (!modalState) {
    return null;
  }

  const modalTitle = getPatientSupportModalTitle(modalState.kind);
  const policyDocument =
    modalState.kind === "privacy" || modalState.kind === "terms"
      ? patientSupportPolicyDocuments[modalState.kind]
      : null;
  const isPolicyModal = policyDocument != null;
  const isBugReport = modalState.kind === "bug";
  const submitLabel = formState.isSubmitting
    ? "Submitting"
    : isBugReport
      ? "Submit report"
      : "Submit";
  const introText = isBugReport
    ? "Found a bug? Let us know. Your feedback helps us improve the app and make the experience smoother for everyone."
    : modalState.kind === "chat-feedback"
      ? `Tell us how this ${brand.assistantName} reply worked for you. Your notes help improve future answers.`
      : "We value your feedback. Help us improve the app by sharing what you love and what we could do better.";

  const renderFormField = (fieldInput: {
    field: PatientSupportFormField;
    labelText: string;
    placeholderText?: string;
    descriptionText?: string;
    isRequired?: boolean;
    isMultiline?: boolean;
    maxLength?: number;
  }) => (
    <View style={[styles.patientSupportField, { gap: 6 }]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: "700",
          },
        ]}
      >
        {fieldInput.labelText}
        {fieldInput.isRequired ? " *" : ""}
      </Text>
      {fieldInput.descriptionText ? (
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              lineHeight: tokens.typography.lineHeight.sm,
            },
          ]}
        >
          {fieldInput.descriptionText}
        </Text>
      ) : null}
      <TextInput
        editable={!formState.isSubmitting}
        maxLength={fieldInput.maxLength}
        multiline={fieldInput.isMultiline}
        onChangeText={(value) => onChangeField(fieldInput.field, value)}
        placeholder={fieldInput.placeholderText}
        placeholderTextColor={patientDesktopPalette.placeholderText}
        style={[
          styles.patientSupportInput,
          fieldInput.isMultiline ? styles.patientSupportTextArea : null,
          {
            backgroundColor: patientMobilePalette.fieldBackground,
            borderColor: patientMobilePalette.line,
            borderRadius: 6,
            color: patientMobilePalette.primaryText,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            opacity: formState.isSubmitting ? 0.7 : 1,
          },
        ]}
        value={formState[fieldInput.field]}
      />
    </View>
  );

  return (
    <View style={styles.patientSupportModalOverlay}>
      <Pressable
        accessibilityLabel="Close support form"
        accessibilityRole="button"
        onPress={onClose}
        style={styles.patientSupportModalBackdrop}
      />
      <View
        style={[
          styles.patientSupportModalPanel,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderSubtle,
            borderRadius: 20,
          },
        ]}
      >
        <View
          style={[
          styles.patientSupportModalHeader,
            createWebGradientBackgroundStyle(theme.shellGradient),
            {
              backgroundColor: theme.headerBand,
              paddingHorizontal: tokens.spacing.lg,
              paddingVertical: tokens.spacing.md,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: theme.headerText,
                flex: 1,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.lg,
                lineHeight: tokens.typography.lineHeight.lg,
              },
            ]}
          >
            {modalTitle}
          </Text>
          <Pressable
            accessibilityLabel={`Close ${modalTitle}`}
            onPress={onClose}
            style={[
              styles.patientSupportModalCloseButton,
              {
                backgroundColor: theme.onGradientPillBackground,
                borderColor: theme.onGradientPillBorder,
                borderRadius: 18,
              },
            ]}
          >
            <LineIcon color={theme.headerText} name="x" size={18} strokeWidth={2.4} />
          </Pressable>
        </View>

        <ScrollView
          style={styles.patientSupportModalScroll}
          contentContainerStyle={[
            styles.patientSupportModalContent,
            {
              gap: tokens.spacing.md,
              padding: tokens.spacing.lg,
            },
          ]}
        >
          {formState.successText ? (
            <>
              <Text
                style={[
                  styles.surfaceTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: tokens.typography.fontSize.lg,
                  },
                ]}
              >
                {isBugReport
                  ? "Thank you for reporting the bug!"
                  : "Thank you for your feedback!"}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {formState.successText}
              </Text>
              {formState.receiptId ? (
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: patientDesktopPalette.darkBlue,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      fontWeight: "700",
                    },
                  ]}
                >
                  Receipt {formState.receiptId}
                </Text>
              ) : null}
              <View style={styles.patientSupportButtonRow}>
                <Pressable
                  onPress={onClose}
                  style={[
                    styles.patientSupportPrimaryButton,
                    createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
                    {
                      backgroundColor: patientMobilePalette.actionBlue,
                      borderColor: patientMobilePalette.actionBlue,
                      borderRadius: 20,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: patientMobilePalette.white,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Close
                  </Text>
                </Pressable>
              </View>
            </>
          ) : isPolicyModal && policyDocument ? (
            <>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                Open the current AmpleCare {policyDocument.title.toLowerCase()}.
                The document opens in a new browser tab so you can read or save
                the full policy.
              </Text>
              <View
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: patientDesktopPalette.lightBand,
                    borderColor: patientMobilePalette.line,
                    borderRadius: 8,
                    gap: tokens.spacing.sm,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: patientMobilePalette.primaryText,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                    },
                  ]}
                >
                  {policyDocument.title}
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: patientDesktopPalette.mutedText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  Open the current AmpleCare policy information.
                </Text>
              </View>
              <View style={styles.patientSupportButtonRow}>
                <Pressable
                  onPress={() =>
                    openPatientSupportExternalUrl(policyDocument.markdownUrl)
                  }
                  style={[
                    styles.patientSupportPrimaryButton,
                    createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
                    {
                      backgroundColor: patientMobilePalette.actionBlue,
                      borderColor: patientMobilePalette.actionBlue,
                      borderRadius: 20,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: patientMobilePalette.white,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Open policy
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() =>
                    openPatientSupportExternalUrl(policyDocument.pdfUrl)
                  }
                  style={[
                    styles.patientSupportSecondaryButton,
                    {
                      backgroundColor: patientDesktopPalette.lightBand,
                      borderColor: patientMobilePalette.line,
                      borderRadius: 20,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: patientDesktopPalette.darkBlue,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Open PDF
                  </Text>
                </Pressable>
              </View>
            </>
          ) : (
            <>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                {introText}
              </Text>

              {renderFormField({
                field: "name",
                labelText: "Name",
                maxLength: 100,
              })}
              {renderFormField({
                descriptionText:
                  "Provide if you would like to be contacted about this report.",
                field: "emailAddress",
                labelText: "Email address",
                maxLength: 254,
              })}

              {isBugReport ? (
                <>
                  {renderFormField({
                    field: "bugContext",
                    isRequired: true,
                    labelText: "What were you doing when you noticed the bug?",
                    maxLength: 500,
                    placeholderText:
                      "E.g. I was trying to check my upcoming appointment.",
                  })}
                  {renderFormField({
                    field: "bugImpact",
                    isMultiline: true,
                    isRequired: true,
                    labelText: "How did the bug affect your experience?",
                    maxLength: 1000,
                    placeholderText:
                      "E.g. The appointment details did not show up.",
                  })}
                </>
              ) : (
                renderFormField({
                  field: "feedbackText",
                  isMultiline: true,
                  isRequired: true,
                  labelText: "Feedback",
                  maxLength: 2000,
                })
              )}

              {modalState.message ? (
                <View
                  style={[
                    styles.detailBodyCard,
                    {
                      backgroundColor: patientMobilePalette.fieldBackground,
                      borderColor: patientMobilePalette.line,
                      borderRadius: 8,
                      gap: 6,
                      padding: tokens.spacing.sm,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: patientDesktopPalette.darkBlue,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        fontWeight: "700",
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {`${brand.assistantName} reply`}
                  </Text>
                  <Text
                    numberOfLines={4}
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: patientDesktopPalette.mutedText,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {modalState.message.bodyText}
                  </Text>
                </View>
              ) : null}

              {formState.errorText ? (
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.criticalText,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {formState.errorText}
                </Text>
              ) : null}

              <View style={styles.patientSupportButtonRow}>
                <Pressable
                  disabled={formState.isSubmitting}
                  onPress={onClose}
                  style={[
                    styles.patientSupportSecondaryButton,
                    {
                      backgroundColor: patientDesktopPalette.lightBand,
                      borderColor: patientMobilePalette.line,
                      borderRadius: 20,
                      opacity: formState.isSubmitting ? 0.7 : 1,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: patientDesktopPalette.darkBlue,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    Cancel
                  </Text>
                </Pressable>
                <Pressable
                  disabled={formState.isSubmitting}
                  onPress={onSubmit}
                  style={[
                    styles.patientSupportPrimaryButton,
                    createWebGradientBackgroundStyle(patientMobilePalette.primaryGradient),
                    {
                      backgroundColor: patientMobilePalette.actionBlue,
                      borderColor: patientMobilePalette.actionBlue,
                      borderRadius: 20,
                      opacity: formState.isSubmitting ? 0.75 : 1,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: patientMobilePalette.white,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    {submitLabel}
                  </Text>
                </Pressable>
              </View>

              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    lineHeight: tokens.typography.lineHeight.sm,
                  },
                ]}
              >
                We will never share your personal information. If provided, it
                will only be used to follow up on this submission.
              </Text>
            </>
          )}
        </ScrollView>
      </View>
    </View>
  );
}

function openLegacyWorkbenchFooterLink(linkId: PatientDesktopFooterLinkId): void {
  if (linkId === "terms" || linkId === "privacy") {
    openPatientSupportExternalUrl(
      patientSupportPolicyDocuments[linkId].markdownUrl,
    );
    return;
  }

  if (Platform.OS !== "web" || typeof window === "undefined") {
    return;
  }

  const subject =
    linkId === "bug" ? "AmpleCare issue report" : "AmpleCare feedback";
  window.location.href = `mailto:support@amplecare.ai?subject=${encodeURIComponent(
    subject,
  )}`;
}

export function renderLegacyWorkbenchFooter(input: {
  layout: typeof designTokens.semanticLayout;
  palette: typeof patientDesktopPalette;
  position?: "absolute" | "relative";
  viewportWidth: number;
}) {
  const { layout, palette, position = "absolute", viewportWidth } = input;
  const stacksFooterMeta = viewportWidth < 760;

  return (
    <View
      style={[
        styles.legacyWorkbenchFooter,
        createWebGradientBackgroundStyle(palette.shellGradient),
        {
          backgroundColor: palette.darkBlue,
          paddingHorizontal: viewportWidth < 960 ? 22 : 72,
          position,
        },
      ]}
    >
      <View
        style={[
          styles.patientDesktopFooterMeta,
          {
            flexDirection: stacksFooterMeta ? "column" : "row",
            gap: stacksFooterMeta ? 4 : 18,
            maxWidth: 1600,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: palette.white,
              flexShrink: 0,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 14,
            },
          ]}
        >
          {`${brand.poweredByText ? `${brand.poweredByText} · ` : ""}\u00A9 AmpleCare.ai - All rights reserved.`}
        </Text>

        <View
          style={[
            styles.patientDesktopFooterLinks,
            {
              gap: stacksFooterMeta ? 10 : 18,
              justifyContent: stacksFooterMeta ? "center" : "flex-end",
            },
          ]}
        >
          {patientDesktopFooterLinks.map((link) => (
            <Pressable
              key={link.id}
              onPress={() => openLegacyWorkbenchFooterLink(link.id)}
              style={styles.patientDesktopFooterLink}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: palette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "600",
                    lineHeight: 14,
                  },
                ]}
              >
                {link.labelText}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
}

export function renderPatientDesktopFooter(input: {
  layout: typeof designTokens.semanticLayout;
  onOpenSupportModal: (input: PatientSupportModalState) => void;
  palette: typeof patientDesktopPalette;
  viewportWidth: number;
}) {
  const { layout, onOpenSupportModal, palette, viewportWidth } = input;
  const stacksFooterMeta = viewportWidth < 760;

  return (
    <View
      style={[
        styles.patientDesktopFooter,
        createWebGradientBackgroundStyle(palette.shellGradient),
        {
          backgroundColor: palette.darkBlue,
          paddingHorizontal: viewportWidth < 960 ? 22 : 72,
          paddingVertical: 7,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: palette.white,
            fontFamily: layout.bodyFontFamily,
            fontSize: 15,
            fontWeight: "600",
            lineHeight: 18,
            textAlign: "center",
          },
        ]}
      >
        In case of an emergency, please{" "}
        <Text
          onPress={() => {
            if (Platform.OS === "web" && typeof window !== "undefined") {
              window.location.href = "tel:911";
            }
          }}
          style={{
            color: palette.white,
            fontFamily: layout.bodyFontFamily,
            fontWeight: "800",
            textDecorationLine: "underline",
          }}
        >
          call 911
        </Text>{" "}
        or proceed to the nearest Emergency Room immediately.
      </Text>

      <View
        style={[
          styles.patientDesktopFooterMeta,
          {
            flexDirection: stacksFooterMeta ? "column" : "row",
            gap: stacksFooterMeta ? 4 : 18,
            maxWidth: 1600,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: palette.white,
              flexShrink: 0,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              lineHeight: 14,
            },
          ]}
        >
          {`${brand.poweredByText ? `${brand.poweredByText} · ` : ""}\u00A9 AmpleCare.ai - All rights reserved.`}
        </Text>

        <View
          style={[
            styles.patientDesktopFooterLinks,
            {
              gap: stacksFooterMeta ? 10 : 18,
              justifyContent: stacksFooterMeta ? "center" : "flex-end",
            },
          ]}
        >
          {patientDesktopFooterLinks.map((link) => (
            <Pressable
              key={link.id}
              onPress={() =>
                onOpenSupportModal({
                  kind: link.id,
                  source: `patient-footer:${link.id}`,
                })
              }
              style={styles.patientDesktopFooterLink}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: palette.white,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: "600",
                    lineHeight: 14,
                  },
                ]}
              >
                {link.labelText}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
}
